import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  CreateProductRecipeInput,
  GetProductRecipeResult,
  ListProductRecipeInput,
  ListProductRecipeResult,
  UpdateProductRecipeInput,
} from 'services/interface/productRecipe.interface'
import {
  createProductRecipe,
  deleteProductRecipe,
  getProductRecipe,
  listProductRecipes,
  updateProductRecipe,
} from 'services/productApi'

const RECIPE_LIST_KEY = 'productRecipeList'

export const useListProductRecipes = (
  input: ListProductRecipeInput,
  enabled = true
): UseQueryResult<ListProductRecipeResult, Error> =>
  useQuery({
    queryKey: [RECIPE_LIST_KEY, input],
    queryFn: () => listProductRecipes(input),
    enabled,
  })

export const useGetProductRecipe = (
  id: string | null,
  enabled = true
): UseQueryResult<GetProductRecipeResult, Error> =>
  useQuery({
    queryKey: ['productRecipe', id],
    queryFn: () => getProductRecipe(id!),
    enabled: enabled && !!id,
  })

export const useCreateProductRecipe = (): UseMutationResult<
  unknown,
  Error,
  CreateProductRecipeInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => createProductRecipe(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RECIPE_LIST_KEY] })
    },
  })
}

export const useUpdateProductRecipe = (): UseMutationResult<
  GetProductRecipeResult,
  Error,
  UpdateProductRecipeInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => updateProductRecipe(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [RECIPE_LIST_KEY] })
      queryClient.invalidateQueries({ queryKey: ['productRecipe', variables.id] })
    },
  })
}

export const useDeleteProductRecipe = (): UseMutationResult<
  { success: boolean },
  Error,
  string,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => deleteProductRecipe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RECIPE_LIST_KEY] })
    },
  })
}
